import cloneDeep from 'lodash.clonedeep';
import { getActivitiesWithStudentInfo } from '../../../../services/API';
import { getScreeningWindowsForSchoolYear } from '../../../../services/screeningWindowService';
import PromisePool from '@supercharge/promise-pool'

import { states } from '../../values/enums';
import SRS from '../../../../services/StudentRecordService';
import { getPR } from '../../functions/getPR';
import { adjustPercentile } from '../adjustPercentile';
import { getUserData } from '../../../../services/API';
import StudentRecordService from '@/services/StudentRecordService'
import { CLASSPROGRESS_VIEW_TYPES } from '../../containers/TeacherSelections/ClassProgressViewType';
import { getSchoolYearWithFallbacks } from '../../../../utilities/getSchoolYearWithFallbacks';
import store from '@/store';
import { ASSESSMENT_TYPE_FILTERS } from '../../../../constants/constants';

// Helper function to map assessmentWindows to getScreeningWindowsForSchoolYear shape
function mapAssessmentWindowsToScreeningWindows(assessmentWindows) {
  if (!assessmentWindows) {
    return []
  }

  // Get school year dates using fallback utility
  const state = store.getState();
  const assessmentCalendar = state.calendar?.assessmentCalendar;
  const schoolYearCalendar = state.calendar?.schoolYearCalendar;

  const schoolYearResult = getSchoolYearWithFallbacks(assessmentCalendar, schoolYearCalendar);
  const schoolYearStartDate = new Date(schoolYearResult.startDate);
  
  // Create windows for BOY, MOY, EOY
  const windowsToCreate = [
    { id: 'BOY', season: 'Fall' },
    { id: 'MOY', season: 'Winter' },
    { id: 'EOY', season: 'Spring' }
  ];

  return windowsToCreate.map((targetWindow, index) => {
    const currentDate = new Date();
    
    // Find matching assessment window by tags
    const matchingAssessmentWindow = assessmentWindows.find(window => {
      const tags = window.tags || [];
      return tags.some(tag => tag.includes(targetWindow.id));
    });

    if (matchingAssessmentWindow) {
      // Use the actual assessment window data
      const startDate = new Date(matchingAssessmentWindow.dateFrom);
      const endDate = new Date(matchingAssessmentWindow.dateTo);
      
      // Check if this window is currently active
      const isCurrent = currentDate >= startDate && currentDate <= endDate;
      
      // Check if this window is in the past
      const isPast = endDate < currentDate;
      
      // Extract screeningWindow value from tags
      let screeningWindow = null;
      if (matchingAssessmentWindow.tags) {
        if (Array.isArray(matchingAssessmentWindow.tags)) {
          const windowTag = matchingAssessmentWindow.tags.find(tag => tag === 'BOY' || tag === 'MOY' || tag === 'EOY');
          if (windowTag) {
            screeningWindow = windowTag;
          }
        } else if (typeof matchingAssessmentWindow.tags === 'string') {
          if (matchingAssessmentWindow.tags === 'BOY' || matchingAssessmentWindow.tags === 'MOY' || matchingAssessmentWindow.tags === 'EOY') {
            screeningWindow = matchingAssessmentWindow.tags;
          }
        }
      }
      
      return {
        id: targetWindow.id,
        season: targetWindow.season,
        startDate,
        endDate,
        isCurrent,
        isPast,
        periodId: matchingAssessmentWindow.periodId,
        screeningWindowType: matchingAssessmentWindow.screeningWindowType,
        screeningWindow,
        // Preserve original assessment window data for reference
        originalWindow: matchingAssessmentWindow
      };
    } else {
      // Create mock window with school year start date for both start and end dates
      return {
        id: targetWindow.id,
        season: targetWindow.season,
        startDate: schoolYearStartDate,
        endDate: schoolYearStartDate,
        isCurrent: false,
        isPast: false,
        screeningWindow: null,
        // No original window data since this is a mock
        originalWindow: null
      };
    }
  });
}

export async function fetchStudentTrends(students, language) {
  const studentTrendStudentInput = students.map(s => ({ studentId: s.id, grade: s.grade }));
  const date = new Date();
  
  const trends = await SRS.getStudentTrend(studentTrendStudentInput, date, language);
  if (!trends) return [];
  
  return trends.map(t => ({ ...t, trend: t.trend ? Number(t.trend.toFixed(2)) : t.trend }));
}

export async function fetchActivitiesForClassProgressReport(params) {
  const { students, selectedLocale, assessmentWindows } = params;

  if (!assessmentWindows) {
    return {
      activitiesPerWindow: [],
      benchmarkActivitiesPerWindow: [],
      nonBenchmarkActivitiesPerWindow: []
    }
  }

  const studentIds = students.map(s => s.id);

  // Define hardcoded windows for BOY, MOY, EOY
  const hardCodedWindows = ['BOY', 'MOY', 'EOY'];

  // Create promises for each hardcoded window
  const windowPromises = hardCodedWindows.map(async (windowId) => {
    // Execute both benchmark and non-benchmark calls concurrently for this window
    const [benchmarkActivities, nonBenchmarkActivities] = await Promise.all([
      StudentRecordService.getLatestAssessments({
        studentIds,
        locale: 'EN_US',
        equatedScores: false,
        testType: ASSESSMENT_TYPE_FILTERS.BENCHMARK,
        screeningWindow: windowId
      }),
      StudentRecordService.getLatestAssessments({
        studentIds,
        locale: 'EN_US',
        equatedScores: false,
        testType: ASSESSMENT_TYPE_FILTERS.PROGRESS_MONITORING,
        screeningWindow: windowId
      })
    ]);

    console.log(`Window ${windowId} - Benchmark: ${benchmarkActivities.length}, Non-benchmark: ${nonBenchmarkActivities.length}`);

    return {
      windowId,
      benchmarkActivities,
      nonBenchmarkActivities,
      allActivities: [...benchmarkActivities, ...nonBenchmarkActivities]
    };
  });

  // Execute all window promises concurrently
  const windowResults = await Promise.all(windowPromises);

  // Organize results into the expected format
  const activitiesPerWindow = [];
  const benchmarkActivitiesPerWindow = [];
  const nonBenchmarkActivitiesPerWindow = [];

  windowResults.forEach(result => {
    activitiesPerWindow.push(result.allActivities);
    benchmarkActivitiesPerWindow.push(result.benchmarkActivities);
    nonBenchmarkActivitiesPerWindow.push(result.nonBenchmarkActivities);
  });
  console.log("🚀 ~ fetchActivitiesForClassProgressReport ~ benchmarkActivitiesPerWindow:", benchmarkActivitiesPerWindow)

  // Return organized data with activities separated by window
  return { 
    activitiesPerWindow, 
    benchmarkActivitiesPerWindow, 
    nonBenchmarkActivitiesPerWindow 
  };
}

export async function fetchMasteriesForClassProgressReport(params) {
  const { students, selectedLocale, selectedStatePercentileCuts } = params;
  const studentIds = students.map(student => student.id);
  let useARM = false;
  if (selectedStatePercentileCuts && selectedStatePercentileCuts.state) {
    useARM = selectedStatePercentileCuts.state !== "US";
  }
  const {useLatestARM} = getUserData();
  const readingRopeData = await SRS.getReadingRopeData({ studentIds, locale: selectedLocale, useARM, useLatestARM });
  const batchReadingRopeReport = readingRopeData?.batchReadingRopeReport || [];
  const masteriesByStudentId = {};
  for (const readingRopeReport of batchReadingRopeReport) {
    if (!readingRopeReport) continue;
    const {studentId, threads} = readingRopeReport;
    if (!threads || !Array.isArray(threads)) continue;
    const wordRecognitionThread = threads.find(thread => thread.label === 'Word Recognition');
    const comprehensionThread = threads.find(thread => thread.label === 'Comprehension');
    if (!wordRecognitionThread || !comprehensionThread) continue;
    const phonologicalAwarenessDomain = wordRecognitionThread.domains?.find(domain => domain.label === 'Phonological Awareness');
    const decodingDomain = wordRecognitionThread.domains?.find(domain => domain.label === 'Decoding');
    const vocabularyDomain = comprehensionThread.domains?.find(domain => domain.label === 'Vocabulary');
    if (!phonologicalAwarenessDomain || !decodingDomain || !vocabularyDomain) continue;
    const masteryPRs = {
      phonologicalAwarenessPR: adjustPercentile(phonologicalAwarenessDomain.mastery?.value),
      decodingPR: adjustPercentile(decodingDomain.mastery?.value),
      wordRecognitionPR: adjustPercentile(wordRecognitionThread.mastery?.value),
      vocabularyPR: adjustPercentile(vocabularyDomain.mastery?.value),
      comprehensionPR: adjustPercentile(comprehensionThread.mastery?.value),
    }
    masteriesByStudentId[studentId] = masteryPRs;
  }
  return masteriesByStudentId;
}

// Helper function to extract scores from activities (replaces generateBenchmarkReport logic)
function extractScoresFromActivities(activities, metricId, scaleId, selectedBenchmark, selectedLocale, window) {
  if (!activities || activities.length === 0) {
    return [];
  }

  console.log("🔍 extractScoresFromActivities - activities:", activities);
  console.log("🔍 extractScoresFromActivities - first activity structure:", activities[0]);

  return activities.map(activity => {
    if (!activity.scores || !activity.student) {
      console.log("❌ Activity missing scores or student:", activity);
      return null;
    }
    
    let score = null;
    let PR = null;
    
    // Extract score based on metricId (similar to generateScore function)
    if (metricId === 'ARM') {
      score = activity.scores.armScore;
      PR = activity.scores.armScorePR;
    } else if (metricId === 'ORF') {
      if (scaleId === 'WCPM') {
        score = activity.scores.wcpmScore || activity.scores.equatedWCPM;
      } else if (scaleId === 'Lexile') {
        score = activity.scores.lexileScore;
      } else if (scaleId === 'DRA') {
        score = activity.scores.dra;
      }
    } else if (metricId === 'SightRecognition') {
      score = activity.scores.esriScore;
    } else if (metricId === 'PhonologicalAwareness') {
      score = activity.scores.phonAwareScore;
    } else if (metricId === 'VocabSize') {
      score = activity.scores.vocabSize;
    }
    
    // Only return scores that are valid (not null/undefined)
    if (score === null || score === undefined) return null;
    
    return {
      studentId: activity.studentId,
      firstName: activity.student.firstName,
      lastName: activity.student.lastName,
      displayScore: score,
      PR: PR,
      status: activity.status
    };
  }).filter(score => score !== null);
}

export async function generateClassProgressReport(params) {
  const {
    students,
    metricId,
    scaleId,
    selectedBenchmark,
    customBenchmarks,
    selectedLocale,
    isBenchmarkOnly,
    activities,
    masteries,
    studentTrends,
    benchmarkActivitiesPerWindow,
    nonBenchmarkActivitiesPerWindow,
    classProgressViewType,
    assessmentWindows,
    monthlyData
  } = params;

  // If By Month view is selected, generate monthly report
  if (classProgressViewType === CLASSPROGRESS_VIEW_TYPES.BY_MONTH && monthlyData) {
    return generateMonthlyClassProgressReport(params);
  }

  // Default By Window view (BOY, MOY, EOY)
  return generateWindowClassProgressReport(params);
}

async function generateMonthlyClassProgressReport(params) {
  const {
    students,
    metricId,
    scaleId,
    selectedBenchmark,
    selectedLocale,
    isBenchmarkOnly,
    masteries,
    studentTrends,
    monthlyData
  } = params;

  const reportDataByStudentId = {};
  const studentOrder = [];

  // Process each month's data
  for (const [index, monthData] of monthlyData.entries()) {
    const { period, assessments } = monthData;
    
    console.log(`📅 Processing month ${index}:`, period);
    console.log(`📅 Assessments for month ${index}:`, assessments);
    
    // Extract scores from assessments for this month
    const monthScores = extractScoresFromActivities(assessments, metricId, scaleId, selectedBenchmark, selectedLocale, period);
    
    console.log(`📅 Scores extracted for month ${index}:`, monthScores);
    
    for (const score of monthScores) {
      if (isInvalidScore(score)) continue;
      const {firstName, lastName, studentId, displayScore, PR} = score;
      
      if (!(studentId in reportDataByStudentId)) {
        reportDataByStudentId[studentId] = { 
          name: `${firstName} ${lastName}`,
          studentId 
        };
        studentOrder.push(studentId);
      }
      
      const formattedScore = displayScore ? (metricId === 'ORF' ? Math.round(displayScore) : Math.round(displayScore * 100)/100) : displayScore;
      reportDataByStudentId[studentId][`month_${index}`] = { 
        score: formattedScore, 
        PR, 
        scaleId,
        latestScore: formattedScore // For monthly view, score and latestScore are the same
      };
    }
  }

  // Add students with no activities into report
  for (const student of students) {
    if (!studentOrder.includes(student.id)) {
      studentOrder.push(student.id);
      reportDataByStudentId[student.id] = {
        studentId: student.id,
        name: student.ui.label
      };
    }
  }

  const masteriesByStudentId = masteries[selectedLocale];

  const studentOrderFinal = studentOrder.map(studentId => {
    const reportData = reportDataByStudentId[studentId];
    const masteryData = masteriesByStudentId[studentId];
    
    // Estimated Mastery logic for ARM
    const trend = {};
    if (metricId === 'ARM' && studentTrends) {
      const matchedStudentTrend = studentTrends[selectedLocale].find(t => t.studentId === studentId);
      if (matchedStudentTrend && matchedStudentTrend.trend) {
        trend["EM"] = {       
          PR: getPR({
            score: matchedStudentTrend.trend,
            scale: scaleId,
            grade: selectedBenchmark.grade,
            language: selectedLocale,
            window: 'current',
          }),
          scaleId,
          score: matchedStudentTrend.trend,
        };
      } else {
        trend["EM"] = {};
      }
    }
    
    return { studentId, ...reportData, ...masteryData, ...trend };
  });

  console.log("📊 Final monthly report data:", studentOrderFinal);
  
  return studentOrderFinal.sort((a, b) => {
    // Sort by name for monthly view
    return (a.name || '').localeCompare(b.name || '');
  });
}

async function generateWindowClassProgressReport(params) {
  const {
    students,
    metricId,
    scaleId,
    selectedBenchmark,
    customBenchmarks,
    selectedLocale,
    isBenchmarkOnly,
    activities,
    masteries,
    studentTrends,
    benchmarkActivitiesPerWindow,
    nonBenchmarkActivitiesPerWindow,
    assessmentWindows
  } = params;

  // Map assessmentWindows to the shape that getScreeningWindowsForSchoolYear() outputs
  const windows = mapAssessmentWindowsToScreeningWindows(assessmentWindows);

  const reportDataByStudentId = {};
  const studentOrder = [];

  // builds the overall class progress report for each window column with each column scores.
  // Only process the first 3 windows (BOY, MOY, EOY)
  for (const [index, window] of windows.slice(0, 3).entries()) {
    
    // Get benchmark activities for this specific window
    const windowBenchmarkActivities = benchmarkActivitiesPerWindow[index] || [];
    console.log(`Window ${index} benchmark activities:`, windowBenchmarkActivities);
    
    // Extract benchmark scores directly from benchmark activities (replaces generateBenchmarkReport with isBenchmarkOnly = true)
    const benchmarkScores = extractScoresFromActivities(windowBenchmarkActivities, metricId, scaleId, selectedBenchmark, selectedLocale, window);
    console.log("BENCHMARK SCORES", benchmarkScores);

    for (const benchmarkScore of benchmarkScores) {
      if (isInvalidScore(benchmarkScore)) continue;
      const {firstName, lastName, studentId, displayScore: score, PR} = benchmarkScore;
      if (!(studentId in reportDataByStudentId)) {
        reportDataByStudentId[studentId] = { name: `${firstName} ${lastName}` };
        studentOrder.push(studentId);
      }
      const formattedScore = score ? (metricId === 'ORF' ? Math.round(score) : Math.round(score * 100)/100) : score;
      reportDataByStudentId[studentId][window.id] = { score: formattedScore, PR, scaleId };
    }

    // Get latest scores from all activities for this window (benchmark + non-benchmark)
    // This replaces generateBenchmarkReport with isBenchmarkOnly = false
    const allWindowActivities = [
      ...(benchmarkActivitiesPerWindow[index] || []),
      ...(nonBenchmarkActivitiesPerWindow[index] || [])
    ];
    
    const latestScores = extractScoresFromActivities(allWindowActivities, metricId, scaleId, selectedBenchmark, selectedLocale, window);
    
    for (const latestScore of latestScores) {
      if (isInvalidScore(latestScore)) continue;
      const {studentId, firstName, lastName, displayScore: score} = latestScore;

      if (!(studentId in reportDataByStudentId)) {
        reportDataByStudentId[studentId] = {name: `${firstName} ${lastName}`};
        reportDataByStudentId[studentId][window.id] = {};
        studentOrder.push(studentId);
      }

      const formattedLatestScore = score ? (metricId === 'ORF' ? Math.round(score) : Math.round(score * 100)/100) : score;
      if (!reportDataByStudentId[studentId][window.id]) reportDataByStudentId[studentId][window.id] = {};
      reportDataByStudentId[studentId][window.id].latestScore = formattedLatestScore;
    }
  }

  const activeWindowIndex = windows.findIndex(window => window.isCurrent);
  const activeWindow = windows[activeWindowIndex];
  
  // Get all activities for the active window (benchmark + non-benchmark) for ARM and WCPM scores
  const activeWindowAllActivities = [
    ...(benchmarkActivitiesPerWindow[activeWindowIndex] || []),
    ...(nonBenchmarkActivitiesPerWindow[activeWindowIndex] || [])
  ];
  
  // Extract ARM scores directly (replaces generateBenchmarkReport with isBenchmarkOnly = false)
  const armScores = extractScoresFromActivities(activeWindowAllActivities, "ARM", "ARM", selectedBenchmark, selectedLocale, activeWindow);
  
  // Extract WCPM scores directly (replaces generateBenchmarkReport with isBenchmarkOnly = false)
  const wcpmScores = extractScoresFromActivities(activeWindowAllActivities, "ORF", "WCPM", selectedBenchmark, selectedLocale, activeWindow);

  // include latest ARM PR for thread overview
  for (const armScore of armScores) {
    if (isInvalidScore(armScore)) continue;
    const {studentId, firstName, lastName, PR} = armScore;
    if (!(studentId in reportDataByStudentId)) reportDataByStudentId[studentId] = { name: `${firstName} ${lastName}`};
    reportDataByStudentId[studentId].armPR = PR;
  }

  // include latest WCMP PR for thread overview
  for (const wcpmScore of wcpmScores) {
    if (isInvalidScore(wcpmScore)) continue;
    const {studentId, firstName, lastName, PR} = wcpmScore;
    // Create ui object to match expected format
    const ui = {
      value: studentId,
      label: `${firstName} ${lastName}`
    };
    if (!(studentId in reportDataByStudentId)) reportDataByStudentId[studentId] = {name: ui.label};
    reportDataByStudentId[studentId].wcpmPR = PR;
  }

  // add students with no activities into report
  for (const student of students) {
    if (!studentOrder.includes(student.id)) {
      studentOrder.push(student.id);
      reportDataByStudentId[student.id] = {
        studentId: student.id,
        name: student.ui.label
      };
    }
  }

  const masteriesByStudentId = masteries[selectedLocale];

  
  const studentOrderFinal  =  studentOrder.map(studentId => {
    const reportData = reportDataByStudentId[studentId];
    const masteryData = masteriesByStudentId[studentId];
    // Estimated Mastery logic
    const trend = {}
    if (metricId === 'ARM' && studentTrends) {
      const matchedStudentTrend = studentTrends[selectedLocale].find(t => t.studentId === studentId);
      if (matchedStudentTrend && matchedStudentTrend.trend) {
        const currentWindow = windows.find(w => w.isCurrent);
        if (currentWindow) {
          const window = currentWindow.id;
          trend["EM"] = {       
            PR: getPR({
              score: matchedStudentTrend.trend,
              scale: scaleId,
              grade: selectedBenchmark.grade,
              language: selectedLocale,
              window,
            }),
            scaleId,
            score: matchedStudentTrend.trend,
          };
        }else{
          trend["EM"] = {};
        }
      } else {
        trend["EM"] = {};
      }
    }
    return { studentId, ...reportData, ...masteryData, ...trend }
  });

  return studentOrderFinal.sort((a, b) => {
    // Helper function to check if student has complete assessment data in any window
    const hasCompleteAssessmentData = (student) => {
      const windowIds = ['BOY', 'MOY', 'EOY'];
      for (const windowId of windowIds) {
        const windowData = student[windowId];
        if (windowData && 
            typeof windowData.score === 'number' && 
            typeof windowData.PR === 'number' && 
            windowData.scaleId) {
          return true;
        }
      }
      return false;
    };

    // Helper function to check if student has any meaningful data
    const hasAnyData = (student) => {
      // Check for ARM PR or WCPM PR
      if (typeof student.armPR === 'number' || typeof student.wcpmPR === 'number') return true;
      
      // Check for any window data
      const windowIds = ['BOY', 'MOY', 'EOY'];
      for (const windowId of windowIds) {
        const windowData = student[windowId];
        if (windowData && (
            typeof windowData.score === 'number' || 
            typeof windowData.latestScore === 'number' || 
            typeof windowData.PR === 'number'
        )) {
          return true;
        }
      }
      return false;
    };

    const aHasComplete = hasCompleteAssessmentData(a);
    const bHasComplete = hasCompleteAssessmentData(b);
    const aHasAny = hasAnyData(a);
    const bHasAny = hasAnyData(b);

    // 1. Students with complete assessment data come first
    if (aHasComplete && !bHasComplete) return -1;
    if (!aHasComplete && bHasComplete) return 1;

    // 2. If both have complete data, sort by performance
    if (aHasComplete && bHasComplete) {
      // First priority: ARM PR (higher first)
      if (typeof a.armPR === 'number' && typeof b.armPR === 'number') {
        if (a.armPR !== b.armPR) return b.armPR - a.armPR;
      } else if (typeof a.armPR === 'number') {
        return -1;
      } else if (typeof b.armPR === 'number') {
        return 1;
      }

      // Second priority: Latest window score (EOY > MOY > BOY)
      const windowOrder = ['EOY', 'MOY', 'BOY'];
      for (const windowId of windowOrder) {
        const aWindowData = a[windowId];
        const bWindowData = b[windowId];
        
        if (aWindowData && bWindowData && 
            typeof aWindowData.score === 'number' && 
            typeof bWindowData.score === 'number') {
          if (aWindowData.score !== bWindowData.score) {
            return bWindowData.score - aWindowData.score; // Higher score first
          }
        }
      }

      // Fallback: alphabetical
      return (a.name || '').localeCompare(b.name || '');
    }

    // 3. Students with partial data come second
    if (aHasAny && !bHasAny) return -1;
    if (!aHasAny && bHasAny) return 1;

    // 4. If both have partial data, sort by latest score
    if (aHasAny && bHasAny) {
      const windowOrder = ['EOY', 'MOY', 'BOY'];
      let aLatest = null, bLatest = null;
      
      for (const windowId of windowOrder) {
        if (a[windowId] && typeof a[windowId].latestScore === 'number') {
          aLatest = a[windowId].latestScore;
          break;
        }
      }
      for (const windowId of windowOrder) {
        if (b[windowId] && typeof b[windowId].latestScore === 'number') {
          bLatest = b[windowId].latestScore;
          break;
        }
      }
      
      if (aLatest !== null && bLatest !== null) {
        return bLatest - aLatest; // Higher score first
      } else if (aLatest !== null) {
        return -1;
      } else if (bLatest !== null) {
        return 1;
      }
    }

    // 5. Final fallback: alphabetical by name
    return (a.name || '').localeCompare(b.name || '');
  });
}

function isInvalidScore(score) {
  return score.status === states.ERROR || score.status === "UNDER_REVIEW" || score.displayScore === null;
}